/* Building blocks for the new header search (search-overlay.js): the skeleton and error panels, the machine legend, and category rows' collections. */
(function () {
  'use strict';

  if (theme.searchOverlayParts) return;

  const SKELETON_ROWS = 3;
  // The id the field's aria-controls names; results and zero-state panels carry it too.
  const RESULTS_ID = 'predictive-search-results';
  // The fitment app's machine events (machine-selector.js; the machine sheet raises the first too).
  const MACHINE_SELECTED_EVENT = 'rpm:machine:selected';
  const MACHINE_CLEARED_EVENT = 'rpm:machine:cleared';
  const GARAGE_LOADED_EVENT = 'rpm:garage:loaded';
  // The Intelligems search test's machine events (PredictiveSearch.track): the card tapped, a machine chosen in its sheet, search without one.
  const MACHINE_TAPPED_EVENT = 'rpm_search_machine_tapped';
  const MACHINE_CHOSEN_EVENT = 'rpm_search_machine_chosen';
  const MACHINE_OFF_EVENT = 'rpm_search_machine_off';
  // A category row's collection, by the segment after /collections/, so a localized path (/fr/collections/x) names the same one.
  function collectionHandle(row) {
    const link = row.querySelector('a');
    try {
      const segments = new URL(link.getAttribute('href'), window.location.origin).pathname.split('/');
      const index = segments.indexOf('collections');
      return index === -1 ? '' : segments[index + 1] || '';
    } catch (e) {
      return '';
    }
  }

  // The real results' classes, so the skeleton takes their layout and results replace it without the panel jumping.
  function buildSkeleton() {
    const panel = element('div', 'predictive-search-results predictive-search-results--skeleton', [
      skeletonColumn(1, 'results--queries', () => [element('span', 'search-skeleton')]),
      skeletonColumn(2, 'results--products', () => [
        element('div', 'results-products__image search-skeleton'),
        element('div', 'results-products__info', [element('span', 'search-skeleton'), element('span', 'search-skeleton search-skeleton--short')]),
      ]),
    ]);
    panel.setAttribute('aria-hidden', 'true');
    panel.id = RESULTS_ID;
    return panel;
  }

  function skeletonColumn(group, modifier, rowContent) {
    const rows = Array.from({ length: SKELETON_ROWS }, () => element('li', '', [element('div', 'search-skeleton-row', rowContent())]));
    const heading = element('h3', 'h4', [element('span', 'search-skeleton search-skeleton--heading')]);
    return element('div', `results__group-${group}`, [element('div', `results ${modifier}`, [heading, element('ul', '', rows)])]);
  }

  // The message, and a submit button for the full results page; inside the search form, so it searches what was typed.
  function buildError(message, buttonLabel) {
    const text = element('p');
    text.textContent = message;
    const button = element('button', 'btn results__search-btn');
    button.type = 'submit';
    button.textContent = buttonLabel;
    const panel = element('div', 'predictive-search-results predictive-search-results--error', [element('div', 'results', [text])]);
    panel.id = RESULTS_ID;
    const fragment = document.createDocumentFragment();
    fragment.append(panel, element('div', 'results__footer', [button]));
    return fragment;
  }

  // The shopper's machine; switched off, that one back, or a choice where they have more; or a way to add one (snippets/
  // predictive-search.liquid's templates). Where there's room, a guest's strip ends with the reason to sign in.
  function buildMachine(search, status, roomy) {
    const machine = status.machine || status.off;
    const choices = status.choices || [];
    const state = status.machine ? 'known' : status.off ? (choices.length > 1 ? 'all' : 'off') : 'missing';
    const template = search.querySelector(`template[data-search-machine="${state}"]`);
    if (!template) return null;
    const legend = template.content.firstElementChild.cloneNode(true);
    const icons = theme.searchMachineIcon;
    const icon = legend.querySelector('.search-machine__icon');
    // The "all machines" template names no machine: without the icon module its plain circle stays.
    if (state === 'all') {
      if (icons) icon.replaceWith(icons.buildStack(search, choices.slice(0, 2)));
    } else if (machine && machine.name) {
      legend.querySelector('[data-search-machine-name]').textContent = machine.name;
      if (icons) icon.replaceWith(icons.build(search, machine.name));
    }
    const signIn = roomy && search.querySelector(`template[data-search-sign-in="${machine ? 'known' : 'missing'}"]`);
    if (signIn) legend.appendChild(signIn.content.firstElementChild.cloneNode(true));
    return legend;
  }

  // The machine sheet over search (assets/util.machine-sheet.js), where the fitment app's picker is on the page; without it, or if
  // the sheet can't load, the link leads to a page with the app's selector. A search closed while the sheet waits gets no sheet.
  function openMachineSheet(search, event) {
    const link = event.target.closest('a');
    const template = search.querySelector('template[data-search-machine-sheet]');
    if (!link || !template || !window.RpmDropdown) return;
    event.preventDefault();
    const adding = link.hasAttribute('data-search-machine-add');
    // Its garage rows lead with each machine's make, as the strip does; a pick switches a machine switched off back on.
    const decorate = (dropdown) => {
      if (theme.searchMachineIcon) theme.searchMachineIcon.markGarageRows(search, dropdown);
      if (theme.searchMachineSwitch) theme.searchMachineSwitch.followPicks(dropdown);
    };
    const onChosen = () => search.track(MACHINE_CHOSEN_EVENT);
    const options = { template, opener: search.input, trigger: link, adding, decorate, onChosen, stillWanted: openSinceTap(search) };
    import('util.machine-sheet')
      .then((module) => module.openMachineSheet(options))
      .catch(() => { window.location.href = link.href; });
  }

  // Wanted while the search the tap came from stays open: once that search has closed, even if reopened since, the tap gets no sheet.
  function openSinceTap(search) {
    const closes = search.closeCount || 0;
    return () => (search.closeCount || 0) === closes && isOpen(search);
  }

  // The results panel is open (search-overlay.js setOpen) in the header and on the search page; there, is-active waits for typing.
  function isOpen(search) {
    return search.predictiveSearchResults.classList.contains('is-open');
  }

  function element(tag, className, children) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (children) node.append(...children);
    return node;
  }

  // Delegated, since typed results are serialized into the panel and panels are reused: listeners on a legend itself would be lost.
  function followMachineLinks(search) {
    if (search.followsMachineLinks) return;
    search.followsMachineLinks = true;
    search.predictiveSearchResults.addEventListener('click', (event) => {
      const machineSwitch = event.target.closest('[data-search-machine-switch]');
      if (machineSwitch) switchMachine(search, machineSwitch.getAttribute('data-search-machine-switch') === 'on');
      else if (event.target.closest('.search-machine__link')) {
        search.track(MACHINE_TAPPED_EVENT);
        openMachineSheet(search, event);
      }
    });
    // A machine picked or cleared while search is open re-renders it; the microtask lets search-fitment.js take the change in first.
    const refresh = () => Promise.resolve().then(() => { if (isOpen(search)) search.onChange(); });
    // A newly picked machine's best sellers and categories are fetched for the panels after this one, as search's opening fetches them.
    document.addEventListener(MACHINE_SELECTED_EVENT, () => Promise.resolve().then(() => theme.searchEmptyStates.prefetch(search)));
    document.addEventListener(MACHINE_SELECTED_EVENT, refresh);
    document.addEventListener(MACHINE_CLEARED_EVENT, refresh);
    // A signed-in garage landing late changes what a machine switched off offers back.
    document.addEventListener(GARAGE_LOADED_EVENT, refresh);
  }

  // Search without the machine, or with it again. The card is rebuilt, so focus waits on the panel, then goes to the control that
  // undoes the switch (focusMachineSwitch, as the new panel shows).
  function switchMachine(search, on) {
    const status = theme.searchFitment && theme.searchFitment.machineStatus();
    // A second tap on a control the next panel hasn't replaced yet (results load under it) finds the switch already made.
    if (!status || !theme.searchMachineSwitch || (on ? !status.off : !status.machine)) return;
    if (!on) search.track(MACHINE_OFF_EVENT);
    theme.searchMachineSwitch.set(on ? null : status.machine);
    search.machineSwitchFocus = `[data-search-machine-switch="${on ? 'off' : 'on'}"]`;
    search.predictiveSearchResults.focus({ preventScroll: true });
    search.onChange();
  }

  // Only while focus still waits on the panel: a shopper back in the field by then keeps it there.
  function focusMachineSwitch(search) {
    const selector = search.machineSwitchFocus;
    search.machineSwitchFocus = null;
    const control = selector && search.predictiveSearchResults.querySelector(selector);
    if (control && document.activeElement === search.predictiveSearchResults) control.focus({ preventScroll: true });
  }

  theme.searchOverlayParts = {
    collectionHandle, buildSkeleton, buildError, buildMachine, followMachineLinks, focusMachineSwitch, element,
  };
})();
